import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Menu, X, ChevronDown } from "lucide-react";
import { GROUPS } from "@/lib/products";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/#about" },
  { label: "Products", to: "/catalogue", dropdown: true },
  { label: "Certifications", to: "/#compliance" },
  { label: "Markets", to: "/#export" },
  { label: "Contact", to: "/#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileProducts, setMobileProducts] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkColor = scrolled || open ? "text-navy/70 hover:text-navy" : "text-white/80 hover:text-white";

  return (
    <header
      data-testid="main-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled || open
          ? "bg-white/90 shadow-[0_1px_0_0_rgba(10,37,64,0.08)] backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[92px] max-w-7xl items-center justify-between px-5 md:px-10">
        <Link to="/" data-testid="brand-mark" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <img src="/assets/brand/logo.png" alt="RBL EXIM logo" className="h-14 w-14 object-contain md:h-16 md:w-16" />
          <span className={`font-display text-lg font-semibold tracking-tight transition-colors duration-300 ${scrolled || open ? "text-navy" : "text-white"}`}>
            RBL EXIM
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="main-nav">
          {NAV.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="group relative">
                <Link
                  to={item.to}
                  data-testid="nav-link-products"
                  className={`group/link relative flex items-center gap-1.5 text-sm font-medium transition-colors duration-300 ${linkColor}`}
                >
                  Products
                  <ChevronDown size={15} className="transition-transform duration-300 group-hover:rotate-180" />
                  <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-gold transition-[width] duration-300 group-hover/link:w-full" />
                </Link>
                <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-4 opacity-0 transition-[opacity,visibility] duration-200 group-hover:visible group-hover:opacity-100">
                  <div
                    data-testid="products-dropdown"
                    className="w-72 rounded-2xl border border-navy/10 bg-white p-2 shadow-xl shadow-navy/10"
                  >
                    <Link
                      to="/catalogue"
                      className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-cream"
                    >
                      All Products
                    </Link>
                    {GROUPS.map((g) => (
                      <Link
                        key={g.slug}
                        to={`/products/${g.slug}`}
                        data-testid={`dropdown-${g.slug}`}
                        className="block rounded-xl px-4 py-2.5 text-sm text-navy/70 transition-colors duration-200 hover:bg-cream hover:text-navy"
                      >
                        {g.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`group relative text-sm font-medium transition-colors duration-300 ${linkColor}`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/#enquire"
            data-testid="header-quote-button"
            className="group hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-navy hover:text-white sm:flex"
          >
            Request a Quote
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
            className={`lg:hidden ${scrolled || open ? "text-navy" : "text-white"}`}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <nav data-testid="mobile-nav" className="max-h-[70vh] overflow-y-auto border-t border-navy/10 bg-white px-5 py-4 lg:hidden">
          {NAV.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="border-b border-navy/5">
                <button
                  onClick={() => setMobileProducts((v) => !v)}
                  data-testid="mobile-products-toggle"
                  className="flex w-full items-center justify-between py-3.5 text-base font-medium text-navy"
                >
                  Products
                  <ChevronDown size={17} className={`transition-transform duration-300 ${mobileProducts ? "rotate-180" : ""}`} />
                </button>
                {mobileProducts && (
                  <div className="pb-3">
                    <Link to="/catalogue" onClick={() => setOpen(false)} className="block py-2.5 pl-4 text-sm font-semibold text-navy">
                      All Products
                    </Link>
                    {GROUPS.map((g) => (
                      <Link
                        key={g.slug}
                        to={`/products/${g.slug}`}
                        onClick={() => setOpen(false)}
                        className="block py-2.5 pl-4 text-sm text-navy/70"
                      >
                        {g.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block border-b border-navy/5 py-3.5 text-base font-medium text-navy"
              >
                {item.label}
              </Link>
            )
          )}
          <Link
            to="/#enquire"
            onClick={() => setOpen(false)}
            data-testid="mobile-quote-button"
            className="mt-4 flex items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-semibold text-navy"
          >
            Request a Quote <ArrowUpRight size={16} />
          </Link>
        </nav>
      )}
    </header>
  );
}
